/**
 * ColourMatch Pro · Painter Business Profile settings.
 *
 * Phase 7 (2026-07-07) — SSOT for every painter-branded value that
 * appears on generated documents (colour schedules, quotes, invoices,
 * warranty docs, completion certificates and any future export).
 *
 * The screen deliberately reads/writes only through the
 * `/api/pro/business-profile` endpoints — no other Pro page should
 * duplicate a business field. When a future module needs, say, the
 * painter's phone number, it fetches it from here.
 */

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Building2, Upload, Loader2, ShieldCheck, Trash2, CheckCircle2, Circle } from "lucide-react";
import api from "@/lib/api";
import usePageMeta from "@/hooks/usePageMeta";

const TEXT_INPUT =
  "w-full h-11 px-3 border border-brand-linen bg-white text-sm text-brand-ink " +
  "focus:outline-none focus:border-brand-teal transition-colors";
const TEXTAREA =
  "w-full min-h-[90px] px-3 py-2 border border-brand-linen bg-white text-sm text-brand-ink " +
  "focus:outline-none focus:border-brand-teal transition-colors resize-y";
const LABEL = "block text-xs uppercase tracking-wide text-brand-muted mb-1.5";
const SECTION = "border border-brand-linen bg-white p-6 space-y-4";
const SECTION_TITLE = "font-serif text-lg text-brand-ink";
const SECTION_MUTED = "text-xs text-brand-muted";

export default function PainterSettings() {
  usePageMeta({
    title: "Painter business profile — ColourMatch Pro",
    description:
      "The single source of truth for your business name, logo, contact details, licence, ABN and insurance. Populates every colour schedule, quote and future document.",
    path: "/pro/settings",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileRef = useRef(null);

  // ------------------------------------------------------------------
  useEffect(() => {
    let alive = true;
    api
      .get("/pro/business-profile")
      .then((r) => {
        if (!alive) return;
        setProfile(r.data);
        setForm(hydrate(r.data));
      })
      .catch((e) => {
        // Owner accounts + brand-new painters have no business_profile yet;
        // show a clean empty editor instead of a blocking error toast.
        if (e?.response?.status === 404) return;
        toast.error(e.response?.data?.detail || "Could not load your business profile.");
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  function hydrate(p) {
    return {
      business_name: p.business_name || "",
      abn: p.abn || "",
      licence_number: p.licence_number || "",
      phone: p.phone || "",
      email: p.email || "",
      website: p.website || "",
      address: p.address || "",
      service_area: p.service_area || "",
      insurance_verified: !!p.insurance_verified,
      insurance_notes: p.insurance_notes || "",
      social: {
        instagram: p.social?.instagram || "",
        facebook: p.social?.facebook || "",
        linkedin: p.social?.linkedin || "",
        tiktok: p.social?.tiktok || "",
        youtube: p.social?.youtube || "",
      },
    };
  }

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }
  function setSocial(key, value) {
    setForm((f) => ({ ...f, social: { ...f.social, [key]: value } }));
  }

  async function handleSave(e) {
    e?.preventDefault?.();
    if (!form) return;
    setSaving(true);
    try {
      // Send only the fields the user could set — the API accepts partials.
      const payload = {
        business_name: form.business_name || null,
        abn: form.abn || null,
        licence_number: form.licence_number || null,
        phone: form.phone || null,
        email: form.email || null,
        website: form.website || null,
        address: form.address || null,
        service_area: form.service_area || null,
        insurance_verified: form.insurance_verified,
        insurance_notes: form.insurance_notes || null,
        social: {
          instagram: form.social.instagram || null,
          facebook: form.social.facebook || null,
          linkedin: form.social.linkedin || null,
          tiktok: form.social.tiktok || null,
          youtube: form.social.youtube || null,
        },
      };
      const r = await api.patch("/pro/business-profile", payload);
      setProfile(r.data);
      setForm(hydrate(r.data));
      toast.success("Business profile saved.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  async function handleLogoUpload(file) {
    if (!file) return;
    if (file.size > 800 * 1024) {
      toast.error("Logo must be under 800KB. Consider compressing the image.");
      return;
    }
    setUploadingLogo(true);
    try {
      const dataUrl = await fileToDataURL(file);
      const r = await api.post("/pro/business-profile/logo", { logo_data_url: dataUrl });
      setProfile(r.data);
      toast.success("Logo uploaded.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Logo upload failed.");
    } finally {
      setUploadingLogo(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleLogoClear() {
    if (!window.confirm("Remove the current logo?")) return;
    try {
      await api.delete("/pro/business-profile/logo");
      setProfile((p) => ({ ...p, business_logo_url: null }));
      toast.success("Logo removed.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove logo.");
    }
  }

  // ------------------------------------------------------------------
  if (loading || !form) {
    return (
      <div className="flex items-center gap-2 text-sm text-brand-muted" data-testid="painter-settings-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your business profile…
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="painter-settings">
      {/* -- Header ------------------------------------------------------- */}
      <div>
        <div className="overline mb-2 text-brand-teal">Settings</div>
        <h1 className="font-serif text-3xl text-brand-ink">Business profile</h1>
        <p className={`${SECTION_MUTED} mt-2 max-w-2xl`}>
          These details populate every document ColourMatch Pro generates — colour schedules
          today, quotes, invoices, warranty and completion certificates as those modules land.
          Fill in what you have; you can always come back later.
        </p>
      </div>

      {/* -- Completion indicator --------------------------------------- */}
      <CompletionCard completion={profile?.completion} />

      <form onSubmit={handleSave} className="space-y-6">
        {/* -- Logo + Business name ------------------------------------- */}
        <section className={SECTION} data-testid="settings-section-brand">
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1">
              <h2 className={SECTION_TITLE}>Business identity</h2>
              <p className={SECTION_MUTED}>How your business appears on every generated document.</p>
            </div>
            <Building2 className="w-6 h-6 text-brand-teal flex-shrink-0" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-6 pt-2">
            {/* Logo preview + upload */}
            <div>
              <label className={LABEL}>Logo</label>
              <div
                className="w-[140px] h-[140px] border border-brand-linen bg-brand-linen/20 flex items-center justify-center overflow-hidden"
                data-testid="painter-settings-logo-preview"
              >
                {profile?.business_logo_url ? (
                  <img
                    src={profile.business_logo_url}
                    alt="Business logo"
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <span className="text-xs text-brand-muted">No logo yet</span>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                onChange={(e) => handleLogoUpload(e.target.files?.[0])}
                className="hidden"
                data-testid="painter-settings-logo-input"
              />
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploadingLogo}
                  data-testid="painter-settings-logo-upload"
                  className="inline-flex items-center gap-1.5 text-xs h-8 px-3 border border-brand-teal/30 text-brand-teal hover:bg-brand-teal/10 transition-colors disabled:opacity-50"
                >
                  {uploadingLogo ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Upload className="w-3.5 h-3.5" />
                  )}
                  Upload
                </button>
                {profile?.business_logo_url && (
                  <button
                    type="button"
                    onClick={handleLogoClear}
                    data-testid="painter-settings-logo-clear"
                    className="inline-flex items-center gap-1.5 text-xs h-8 px-3 border border-brand-linen text-brand-muted hover:text-brand-ink"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Remove
                  </button>
                )}
              </div>
            </div>

            {/* Text fields */}
            <div className="space-y-4">
              <div>
                <label className={LABEL}>Business name</label>
                <input
                  className={TEXT_INPUT}
                  value={form.business_name}
                  onChange={(e) => setField("business_name", e.target.value)}
                  placeholder="e.g. Coastal Painters Co"
                  data-testid="painter-settings-business-name"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={LABEL}>ABN (optional)</label>
                  <input
                    className={TEXT_INPUT}
                    value={form.abn}
                    onChange={(e) => setField("abn", e.target.value)}
                    placeholder="12 345 678 901"
                    data-testid="painter-settings-abn"
                  />
                </div>
                <div>
                  <label className={LABEL}>Licence number (optional)</label>
                  <input
                    className={TEXT_INPUT}
                    value={form.licence_number}
                    onChange={(e) => setField("licence_number", e.target.value)}
                    placeholder="e.g. NSW-P-2245"
                    data-testid="painter-settings-licence"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* -- Contact -------------------------------------------------- */}
        <section className={SECTION} data-testid="settings-section-contact">
          <h2 className={SECTION_TITLE}>Contact</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>Phone</label>
              <input
                className={TEXT_INPUT}
                value={form.phone}
                onChange={(e) => setField("phone", e.target.value)}
                placeholder="0400 000 000"
                data-testid="painter-settings-phone"
              />
            </div>
            <div>
              <label className={LABEL}>Email</label>
              <input
                type="email"
                className={TEXT_INPUT}
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                placeholder="hello@yourbusiness.au"
                data-testid="painter-settings-email"
              />
            </div>
            <div>
              <label className={LABEL}>Website (optional)</label>
              <input
                className={TEXT_INPUT}
                value={form.website}
                onChange={(e) => setField("website", e.target.value)}
                placeholder="yourbusiness.au"
                data-testid="painter-settings-website"
              />
            </div>
            <div>
              <label className={LABEL}>Service area</label>
              <input
                className={TEXT_INPUT}
                value={form.service_area}
                onChange={(e) => setField("service_area", e.target.value)}
                placeholder="Northern Beaches, NSW"
                data-testid="painter-settings-service-area"
              />
            </div>
          </div>
          <div>
            <label className={LABEL}>Business address</label>
            <textarea
              className={TEXTAREA}
              value={form.address}
              onChange={(e) => setField("address", e.target.value)}
              placeholder="Suite / unit · street · suburb · state · postcode"
              data-testid="painter-settings-address"
            />
          </div>
        </section>

        {/* -- Insurance ------------------------------------------------ */}
        <section className={SECTION} data-testid="settings-section-insurance">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h2 className={SECTION_TITLE}>Insurance</h2>
              <p className={SECTION_MUTED}>
                Displayed as a badge on your professional documents. Toggle on once your public
                liability policy is in place.
              </p>
            </div>
            <ShieldCheck
              className={`w-6 h-6 ${form.insurance_verified ? "text-brand-teal" : "text-brand-muted"}`}
            />
          </div>
          <div className="pt-2 space-y-3">
            <label className="inline-flex items-center gap-2 text-sm text-brand-ink cursor-pointer">
              <input
                type="checkbox"
                checked={form.insurance_verified}
                onChange={(e) => setField("insurance_verified", e.target.checked)}
                data-testid="painter-settings-insurance-verified"
              />
              I confirm my business currently holds valid public liability insurance.
            </label>
            <div>
              <label className={LABEL}>Policy notes (optional)</label>
              <textarea
                className={TEXTAREA}
                value={form.insurance_notes}
                onChange={(e) => setField("insurance_notes", e.target.value)}
                placeholder="e.g. Insurer, policy number, cover amount, expiry."
                data-testid="painter-settings-insurance-notes"
              />
            </div>
          </div>
        </section>

        {/* -- Social --------------------------------------------------- */}
        <section className={SECTION} data-testid="settings-section-social">
          <h2 className={SECTION_TITLE}>Social (optional)</h2>
          <p className={SECTION_MUTED}>
            Only appear on documents that include a social block. Nothing is auto-followed or shared.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {["instagram", "facebook", "linkedin", "tiktok", "youtube"].map((k) => (
              <div key={k}>
                <label className={LABEL}>{k[0].toUpperCase() + k.slice(1)}</label>
                <input
                  className={TEXT_INPUT}
                  value={form.social[k]}
                  onChange={(e) => setSocial(k, e.target.value)}
                  placeholder={`Your ${k} handle or URL`}
                  data-testid={`painter-settings-social-${k}`}
                />
              </div>
            ))}
          </div>
        </section>

        {/* -- Save ----------------------------------------------------- */}
        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={saving}
            data-testid="painter-settings-save"
            className="inline-flex items-center gap-2 h-11 px-6 bg-brand-navy text-white text-sm font-medium hover:bg-brand-teal/90 transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Save profile
          </button>
          <span className="text-xs text-brand-muted">
            Updates apply to every future document immediately — no re-export needed.
          </span>
        </div>
      </form>
    </div>
  );
}

function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read failed"));
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });
}

// ---------------------------------------------------------------------------
// Completion indicator
// ---------------------------------------------------------------------------
function CompletionCard({ completion }) {
  if (!completion) return null;
  const pct = Math.max(0, Math.min(100, completion.percent || 0));
  const missing = completion.missing || [];
  const complete = pct >= 100;

  const barColor = complete
    ? "bg-brand-navy"
    : pct >= 60
    ? "bg-brand-navy/70"
    : "bg-amber-500/80";

  return (
    <section
      className="border border-brand-linen bg-white p-6"
      data-testid="painter-settings-completion"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-brand-muted mb-1">
            Business profile
          </div>
          <div className="flex items-baseline gap-2">
            <div
              className="font-serif text-3xl text-brand-ink"
              data-testid="painter-settings-completion-percent"
            >
              {pct}%
            </div>
            <div className="text-sm text-brand-muted">
              {complete ? "Complete" : "Complete"}
            </div>
          </div>
        </div>
        {complete && (
          <div className="inline-flex items-center gap-1.5 text-xs text-brand-teal border border-brand-teal/30 bg-brand-teal/10 px-2.5 h-7">
            <CheckCircle2 className="w-3.5 h-3.5" /> Ready for professional documents
          </div>
        )}
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-brand-linen/60 overflow-hidden mb-4" aria-hidden>
        <div
          className={`h-full ${barColor} transition-all duration-500`}
          style={{ width: `${pct}%` }}
          data-testid="painter-settings-completion-bar"
        />
      </div>

      {!complete && (
        <div>
          <div className="text-xs uppercase tracking-wide text-brand-muted mb-2">
            Still to add ({missing.length})
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
            {missing.map((m) => (
              <li
                key={m.key}
                className="flex items-center gap-2 text-sm text-brand-ink"
                data-testid={`painter-settings-missing-${m.key}`}
              >
                <Circle className="w-3.5 h-3.5 text-brand-muted" />
                {m.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
